import { Injectable, EventEmitter } from '@angular/core';
import { RxStompConfig, RxStomp, RxStompState, ReconnectionTimeMode } from '@stomp/rx-stomp';
import { v4 as uuid4 } from 'uuid';
import { Subscription, BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { EmitterService } from './emitter.service';
import { AuthTokenUpdatedEvent } from '../models/events';
import { AppTokenRenewalService } from '../auth/app-token-renewal.service';

/* ----------- * * ----------- */
export const WS_STATUS_CLOSED: string = 'CLOSED';
export const WS_STATUS_CONNECTING: string = 'CONNECTING';
export const WS_STATUS_CONNECTED: string = 'CONNECTED';
/* ----------- * * ----------- */
export const listenerWSConnectionChange: EventEmitter<string> = new EventEmitter<string>();
export const wsListenerAUTH_USER_NOTIFY: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerAUTH_USER_POPUP_NOTIFY: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerACTIVE_PROJECT_USERS: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerACTIVE_PROJECT_CONVERSATIONS: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerPROPOSAL_CONVERSATIONS: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerACTIVE_PROJECT_CONVERSATION_USERS: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerPROJECT_CONVERSATION: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerCOMMENT_ADD: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerPROPOSAL_COMMENT_ADD: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerPROPOSAL_QA_COMMENT_ADD: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerVOTE_ADD: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerANNOTATION_ADDED: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerUNSEEN_NOTIFICATION_UPDATED: EventEmitter<any> = new EventEmitter();
export const wsListenerWORKSPACE_DELETE: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerCONVERSION_UPDATE: EventEmitter<any> = new EventEmitter<any>();

export const wsListenerANNOTATION_CREATED: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerANNOTATION_EDITED: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerANNOTATION_DELETED: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerCOMMENT_CREATED: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerCOMMENT_EDITED: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerCOMMENT_DELETED: EventEmitter<any> = new EventEmitter<any>();

export const wsListenerCONVERSATION: EventEmitter<any> = new EventEmitter<any>();

export const wsListenerCHAT_ADDED: EventEmitter<any> = new EventEmitter<any>();
export const wsListenerUSER_UPDATED: EventEmitter<any> = new EventEmitter<any>();

/* ----------- * * ----------- */

export const WS_SEND_DESTINATION_TYPES: Record<string, string> = {
  ONLINE_USER: 'ONLINE_USER',
  PROJECT_CONVERSATION_JOIN: 'PROJECT_CONVERSATION_JOIN',
  ACTIVE_PROJECT_CONVERSATION: 'ACTIVE_PROJECT_CONVERSATION',
  PROPOSAL_MESSAGE: 'PROPOSAL_MESSAGE',
  ACTIVE_PROJECT: 'ACTIVE_PROJECT',
};

export const WS_SUBSCRIBE_TOPIC_TYPES: Record<string, string> = {
  USER_NOTIFY: 'USER_NOTIFY',
  USER_POPUP_NOTIFY: 'USER_POPUP_NOTIFY',
  ACTIVE_PROJECT_USERS: 'ACTIVE_PROJECT_USERS',
  ACTIVE_PROJECT_CONVERSATIONS: 'ACTIVE_PROJECT_CONVERSATIONS',
  PROPOSAL_CONVERSATIONS: 'PROPOSAL_CONVERSATIONS',
  ACTIVE_PROJECT_CONVERSATION_USERS: 'ACTIVE_PROJECT_CONVERSATION_USERS',
  PROJECT_CONVERSATION: 'PROJECT_CONVERSATION',
  NEW_COMMENT_ADDED: 'NEW_COMMENT_ADDED',
  PROPOSAL_COMMENT_ADDED: 'PROPOSAL_COMMENT_ADDED',
  PROPOSAL_QA_COMMENT_ADDED: 'PROPOSAL_QA_COMMENT_ADDED',
  VOTE_ADDED: 'VOTE_ADDED',
  NEW_ANNOTATION_ADDED: 'NEW_ANNOTATION_ADDED',
  NEW_CHAT_MESSAGE: 'NEW_CHAT_MESSAGE',
  USER_UPDATED: 'USER_UPDATED',
  UNSEEN_NOTIFICATION_UPDATED: 'UNSEEN_NOTIFICATION_UPDATED',
  COMMENT_DELETED: 'COMMENT_DELETED',
  ANNOTATION_CREATED: 'ANNOTATION_CREATED',
  ANNOTATION_EDITED: 'ANNOTATION_EDITED',
  ANNOTATION_DELETED: 'ANNOTATION_DELETED',
  COMMENT_CREATED: 'COMMENT_CREATED',
  COMMENT_EDITED: 'COMMENT_EDITED',
  CONVERSATION: 'CONVERSATION',
  WORKSPACE_DELETE: 'WORKSPACE_DELETE',
  CONVERSION_UPDATE: 'CONVERSION_UPDATE',
};

/* ----------- * * ----------- */

export const WS_SEND_DESTINATION_TOPICS: Record<string, string> = {
  [WS_SEND_DESTINATION_TYPES.ONLINE_USER]: `/mtm/active`,
  [WS_SEND_DESTINATION_TYPES.PROJECT_CONVERSATION_JOIN]: `/mtm/projects.{0}.conversations.{1}.join`,
  [WS_SEND_DESTINATION_TYPES.ACTIVE_PROJECT_CONVERSATION]: `/mtm/active-project-conversation.{0}.{1}`,
  [WS_SEND_DESTINATION_TYPES.PROPOSAL_MESSAGE]: `/topic/proposals.{0}`,
  [WS_SEND_DESTINATION_TYPES.ACTIVE_PROJECT]: `/mtm/active-project.{0}`,
};

export const WS_SUBSCRIBE_TOPICS: any = {
  [WS_SUBSCRIBE_TOPIC_TYPES.USER_POPUP_NOTIFY]: {
    topicRef: '/topic/notify.popup.user.{0}',
    wsListener: wsListenerAUTH_USER_POPUP_NOTIFY,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.USER_NOTIFY]: {
    topicRef: '/topic/notify.user.{0}',
    wsListener: wsListenerAUTH_USER_NOTIFY,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.UNSEEN_NOTIFICATION_UPDATED]: {
    topicRef: '/topic/notify.user.{0}.count',
    wsListener: wsListenerUNSEEN_NOTIFICATION_UPDATED,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.ACTIVE_PROJECT_USERS]: {
    topicRef: `/topic/active.project.users.{0}`,
    wsListener: wsListenerACTIVE_PROJECT_USERS,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.ACTIVE_PROJECT_CONVERSATIONS]: {
    topicRef: `/topic/active.project.conversations.{0}`,
    wsListener: wsListenerACTIVE_PROJECT_CONVERSATIONS,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.PROPOSAL_CONVERSATIONS]: {
    topicRef: `/topic/proposals.{0}`,
    wsListener: wsListenerPROPOSAL_CONVERSATIONS,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.ACTIVE_PROJECT_CONVERSATION_USERS]: {
    topicRef: `/topic/active.project.conversation.users.{0}.{1}`,
    wsListener: wsListenerACTIVE_PROJECT_CONVERSATION_USERS,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.PROJECT_CONVERSATION]: {
    topicRef: `/topic/projects.{0}.conversations.{1}`,
    wsListener: wsListenerPROJECT_CONVERSATION,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.NEW_COMMENT_ADDED]: {
    topicRef: '/topic/comments.{0}.{1}.{2}',
    wsListener: wsListenerCOMMENT_ADD,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.PROPOSAL_COMMENT_ADDED]: {
    topicRef: '/topic/proposal-discussion.{0}.{1}',
    wsListener: wsListenerPROPOSAL_COMMENT_ADD,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.PROPOSAL_QA_COMMENT_ADDED]: {
    topicRef: '/topic/proposal-qa.{0}.{1}',
    wsListener: wsListenerPROPOSAL_QA_COMMENT_ADD,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.VOTE_ADDED]: {
    topicRef: '/topic/votes.{0}.{1}.{2}.{3}',
    wsListener: wsListenerVOTE_ADD,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.NEW_ANNOTATION_ADDED]: {
    topicRef: '/topic/media-annotations.{0}.{1}.{2}',
    wsListener: wsListenerANNOTATION_ADDED,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.ANNOTATION_CREATED]: {
    topicRef: '/topic/mediaAnnotations.annotationCreated.{0}.{1}.{2}',
    wsListener: wsListenerANNOTATION_CREATED,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.ANNOTATION_EDITED]: {
    topicRef: '/topic/mediaAnnotations.annotationEdited.{0}.{1}.{2}',
    wsListener: wsListenerANNOTATION_EDITED,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.ANNOTATION_DELETED]: {
    topicRef: '/topic/mediaAnnotations.annotationDeleted.{0}.{1}.{2}',
    wsListener: wsListenerANNOTATION_DELETED,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.COMMENT_CREATED]: {
    topicRef: '/topic/mediaAnnotations.commentCreated.{0}.{1}.{2}',
    wsListener: wsListenerCOMMENT_CREATED,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.COMMENT_EDITED]: {
    topicRef: '/topic/mediaAnnotations.commentEdited.{0}.{1}.{2}',
    wsListener: wsListenerCOMMENT_EDITED,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.COMMENT_DELETED]: {
    topicRef: '/topic/mediaAnnotations.commentDeleted.{0}.{1}.{2}',
    wsListener: wsListenerCOMMENT_DELETED,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.NEW_CHAT_MESSAGE]: {
    topicRef: '/topic/projects.{0}.conversations.user.{1}',
    wsListener: wsListenerCHAT_ADDED,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.USER_UPDATED]: {
    topicRef: '/topic/user.{0}',
    wsListener: wsListenerUSER_UPDATED,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.CONVERSATION]: {
    topicRef: '/topic/conversation.user.{0}',
    wsListener: wsListenerCONVERSATION,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.WORKSPACE_DELETE]: {
    topicRef: '/topic/workspace.delete.user.{0}',
    wsListener: wsListenerWORKSPACE_DELETE,
  },
  [WS_SUBSCRIBE_TOPIC_TYPES.CONVERSION_UPDATE]: {
    topicRef: '/topic/conversionUpdate',
    wsListener: wsListenerCONVERSION_UPDATE,
  },
};

/* ----------- * * ----------- */

@Injectable({
  providedIn: 'root',
})
export class MtmStompService {
  stomp: RxStomp;
  subscriptions: any = {};
  subscriptionCount: any = {};
  records: Record<string, { topic: string; listener: Subscription; topicParams: any[] }[]>;
  private socketConnectionRequired: boolean = true;
  private readonly connectionState: BehaviorSubject<RxStompState> = new BehaviorSubject<RxStompState>(
    RxStompState.CLOSED
  );

  constructor(
    private readonly authService: AuthService,
    private readonly appTokenRenewal: AppTokenRenewalService
  ) {
    this.stomp = new RxStomp();
    this.records = {};

    // Monitor connection state
    this.stomp.connectionState$.subscribe((state) => {
      this.connectionState.next(state);
      this.handleConnectionStateChange(state);
    });

    this.initConnectWS();
    EmitterService.get(AuthTokenUpdatedEvent).subscribe(() => this.initConnectWS());
  }

  private setupConfig(): void {
    const urlObject: URL = new URL(environment.websocket.chatBaseUrl);
    const scheme: string = urlObject.protocol == 'https:' ? 'wss://' : 'ws://';
    const socketUrl: string = scheme + urlObject.host + urlObject.pathname + '/websocket';
    const config: RxStompConfig = {
      brokerURL: socketUrl,
      // The Authorization header is (re)set in beforeConnect on every connect and reconnect: a header
      // captured once here kept the first app token forever, so after it expired every RxStomp
      // auto-reconnect was rejected and the socket looped every 500 ms.
      beforeConnect: (client: RxStomp) => this.refreshConnectHeaders(client),
      heartbeatIncoming: 60000, // Typical value 0 - disabled
      heartbeatOutgoing: 60000, // Typical value 20000 - every 20 seconds
      reconnectDelay: 1000,
      reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
      maxReconnectDelay: 30000,
    };
    this.stomp.configure(config);
  }

  /**
   * Called by RxStomp before each (re)connect. Uses the current app token, renewing it first when it
   * has expired (shared with the HTTP interceptor's renewal). If IAM rejects the renewal the session
   * is gone: stop reconnecting instead of hammering the broker with a dead token.
   */
  private async refreshConnectHeaders(client: RxStomp): Promise<void> {
    if (!this.authService.getAuthToken() || this.authService.isTokenExpired()) {
      const outcome = await firstValueFrom(this.appTokenRenewal.renew());
      if (outcome === 'session-invalid') {
        // stompjs checks `active` right after beforeConnect and skips the connect; not awaited so the
        // hook does not wait on its own teardown.
        void client.deactivate();
        return;
      }
    }
    client.configure({
      connectHeaders: {
        Authorization: 'Bearer ' + this.authService.getAuthToken(),
      },
    });
  }

  public subscribeToService(): string {
    const id: string = uuid4();
    this.records[id] = [];

    return id;
  }

  public subscribeToListener(topic: string, id: string, topicParams: any[], handler: any, topicEvent: any): void {
    this.subscribeToTopic(WS_SUBSCRIBE_TOPIC_TYPES[topic], topicParams);
    const listener: any = topicEvent.subscribe((data) => {
      handler(JSON.parse(data.body || '{}'));
    });

    this.records[id].push({ topic: topic, listener: listener, topicParams: topicParams });
  }

  public unsubscribeToService(id: string): void {
    if (id in this.records) {
      for (const listener of this.records[id]) {
        this.unsubscribeTopic(WS_SUBSCRIBE_TOPIC_TYPES[listener.topic], listener.topicParams);
        listener.listener.unsubscribe();
      }
      delete this.records[id];
    }
  }

  public subscribeToTopic(topic: string, params: (string | number)[]): void {
    const { topicRef, wsListener } = WS_SUBSCRIBE_TOPICS[topic];
    const topicName: string = this.prepareTopic(topicRef, params);
    if (this.subscriptions[topicName]) {
      this.subscriptionCount[topicName]++;
      return;
    }
    this.subscriptions[topicName] = this.stomp.watch(topicName).subscribe((message: any) => {
      wsListener.emit(message);
    });
    this.subscriptionCount[topicName] = 1;
  }

  public unsubscribeTopic(topic: string, params: (string | number)[]): void {
    const { topicRef } = WS_SUBSCRIBE_TOPICS[topic];
    const topicName: string = this.prepareTopic(topicRef, params);
    if (!this.subscriptions[topicName]) {
      return;
    }
    this.subscriptionCount[topicName]--;
    if (this.subscriptionCount[topicName] === 0) {
      this.subscriptions[topicName].unsubscribe();
      delete this.subscriptions[topicName];
      delete this.subscriptionCount[topicName];
    }
  }

  public initConnectWS(): void {
    if (this.connectionState.value === RxStompState.CLOSED && this.hasValidToken()) {
      this.setupConfig();
      this.stomp.activate();
    }
  }

  private hasValidToken(): boolean {
    return !!this.authService.getAuthToken() && !this.authService.isTokenExpired();
  }

  public disconnectWS(): void {
    if (this.connectionState.value !== RxStompState.CLOSED) {
      this.stomp.deactivate();
    }
  }

  public sendToWS(
    sendTopicType: string,
    topicParams: (string | number)[],
    body: any = undefined,
    headers: Record<string, string> = {}
  ): boolean {
    if (this.connectionState.value !== RxStompState.OPEN) {
      return false;
    }

    const topic: string = this.prepareTopic(WS_SEND_DESTINATION_TOPICS[sendTopicType], topicParams);
    this.stomp.publish({
      destination: topic,
      body: body ? JSON.stringify(body) : '',
      headers: headers,
    });

    return true;
  }

  private prepareTopic(topic: string, topicParams: Array<string | number>): string {
    for (const [index, param] of topicParams.entries()) {
      // Use == null to check for both undefined and null; allow values like 0 and ''
      if (param != null) {
        topic = topic.replace(new RegExp(`\\{${index}\\}`, 'g'), param.toString());
      }
    }
    return topic;
  }

  private handleConnectionStateChange(state: RxStompState): void {
    switch (state) {
      case RxStompState.CLOSED:
        listenerWSConnectionChange.emit(WS_STATUS_CLOSED);
        if (this.socketConnectionRequired) {
          setTimeout(() => this.initConnectWS(), 2001);
        }
        break;
      case RxStompState.CONNECTING:
        listenerWSConnectionChange.emit(WS_STATUS_CONNECTING);
        break;
      case RxStompState.OPEN:
        this.socketConnectionRequired = false;
        listenerWSConnectionChange.emit(WS_STATUS_CONNECTED);
        break;
      case RxStompState.CLOSING:
        // Handle closing state if needed
        break;
    }
  }
}
